import * as React from 'react';
import { Button, Typography } from 'omakase';
import { Card, CardActions, CardContent } from 'ui';
import { GroupedOptionOrder } from '@tock/shared/utils/option';
import { core } from '@tock/coretypes';
import { makeStyles } from '@material-ui/core/styles';
import ConsumerDisplayMoney from 'components/ConsumerDisplayMoney';
import _ from 'lodash';

interface GroupedOptionProps {
  entry: GroupedOptionOrder;
  ticketOptionPrices: core.IPrice[];
  onEditOptionOrder(optionOrder: core.IOptionOrder[]): void;
  onRemoveOptionOrder(optionOrder: core.IOptionOrder[]): void;
  disabled?: boolean;
  showPriceInformation?: boolean;
}

const useStyles = makeStyles((theme) => ({
  cardContent: {
    alignItems: 'baseline',
    display: 'flex',
    height: '100%',
    padding: theme.spacing(2),
    paddingBottom: 0,
    width: '100%',
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
  priceList: {
    flex: '1',
  },
  quantity: {
    flex: `0 0 ${theme.typography.pxToRem(24)}`,
    textAlign: 'left',
  },
  itemPrice: {
    alignItems: 'baseline',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    width: '100%',
  },
  itemName: {
    flex: '0 0 60%',
    textAlign: 'left',
  },
  cardDisabled: {
    backgroundColor: theme.colors.bgLight,
    height: '100%',
    marginBottom: theme.spacing(1.5),
    transition: `border-color ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut} 0ms,box-shadow ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut} 0ms,border ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut} 0ms`,
    width: '100%',
    '&:hover': {
      borderColor: theme.colors.borderPrimaryBase,
      '@media (hover: none)': {
        borderColor: 'rgba(0, 0, 0, 0.12)',
      },
    },
  },
  cardActions: {
    paddingLeft: 20,
  },
  card: {
    height: '100%',
    marginBottom: theme.spacing(1.5),
    transition: `border-color ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut} 0ms,box-shadow ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut} 0ms,border ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut} 0ms`,
    width: '100%',
    '&:hover': {
      borderColor: theme.colors.borderPrimaryBase,
      '@media (hover: none)': {
        borderColor: 'rgba(0, 0, 0, 0.12)',
      },
    },
  },
}));

const GroupedOption: React.FunctionComponent<GroupedOptionProps> = ({
  entry,
  ticketOptionPrices,
  onEditOptionOrder,
  onRemoveOptionOrder,
  disabled,
  showPriceInformation = true,
}) => {
  const classes = useStyles();
  const optionOrder = entry.optionOrders[0];
  const price = _.find(ticketOptionPrices, {
    id: optionOrder.optionId,
    backingMode: optionOrder.backingMode,
  });
  const handleEditOptionOrder = () => {
    onEditOptionOrder(entry.optionOrders);
  };
  const handleRemoveOptionOrder = () => {
    onRemoveOptionOrder(entry.optionOrders);
  };

  const buttons = !disabled && (
    <div>
      <Button variant="ghost" size="small" onClick={handleEditOptionOrder}>
        Edit
      </Button>
      <Button variant="danger" size="small" onClick={handleRemoveOptionOrder}>
        Remove
      </Button>
    </div>
  );
  return (
    <Card variant="outlined" className={disabled ? classes.cardDisabled : classes.card}>
      <CardContent className={classes.cardContent}>
        <Typography
          className={classes.quantity}
          component="h2"
          variant="body02Strong"
          color="fgPrimaryBase"
        >
          {entry.numPurchased}
        </Typography>
        <div className={classes.priceList}>
          <div className={classes.itemPrice}>
            <Typography
              className={classes.itemName}
              component="h2"
              variant="body02Strong"
              color="fgSubtle"
              mb={0.5}
            >
              {price?.name}
            </Typography>
            {showPriceInformation ? (
              <Typography variant="body02Strong" component="p" color="fgDefault">
                <ConsumerDisplayMoney amount={price?.amount} />
              </Typography>
            ) : (
              buttons
            )}
          </div>
          {_.map(optionOrder.modifierOption, (modifier, id) => {
            const modifierPrice = _.find(ticketOptionPrices, {
              id: modifier.optionId,
              backingMode: modifier.backingMode,
            });
            return (
              <div key={id} className={classes.itemPrice}>
                <Typography
                  className={classes.itemName}
                  component="h2"
                  variant="body02Strong"
                  color="fgSubtle"
                  mb={0.5}
                >
                  {modifierPrice?.name}
                </Typography>
                {showPriceInformation && (
                  <Typography variant="body02Strong" component="p" color="fgDefault">
                    <ConsumerDisplayMoney amount={modifierPrice?.amount} />
                  </Typography>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
      {showPriceInformation && <CardActions className={classes.cardActions}>{buttons}</CardActions>}
    </Card>
  );
};

export default GroupedOption;
